import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, ArrowLeft, Trophy } from 'lucide-react';
import { RootState, AppDispatch } from '../store';
import { fetchLeaderboard, setSelectedParticipant, fetchParticipantDetails } from '../store/HallOfFameSlice';
import { Leaderboard } from '../components/hall-of-fame/Leaderboard';
import { ReceiptsDashboard } from '../components/hall-of-fame/ReceiptsDashboard';
import { Participant } from '../interface/HallOfFame';
import LoaderHelper from '../utility/Loading/LoaderHelper';
import { SEOMeta, getToolSEOMeta } from '../components/SEOMeta';

export const HallOfFameLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { leaderboardData, loading, error, selectedParticipant } = useSelector(
    (state: RootState) => state.hallOfFame
  );

  // Generate SEO metadata for Hall of Fame page
  const seoMeta = getToolSEOMeta('hall-of-fame');

  useEffect(() => {
    dispatch(fetchLeaderboard({ tier: 'All', page: 0, size: 50 }));
  }, [dispatch]);

  useEffect(() => {
    if (loading) {
      LoaderHelper.loaderStatus(true);
    } else {
      LoaderHelper.loaderStatus(false);
    }
  }, [loading]);

  const handleSelectParticipant = async (participant: Participant) => {
    console.log('🔍 Selected participant:', participant.name, 'ID:', participant.id);
    console.log('🔍 Participant data available:', participant);

    try {
      // Try to fetch detailed data first
      console.log('🔍 Attempting to fetch detailed data...');
      await dispatch(fetchParticipantDetails(participant.id)).unwrap();
      console.log('✅ Detailed data fetched successfully');
    } catch (error) {
      console.log('⚠️ Failed to fetch detailed data, using leaderboard data as fallback');
      console.log('🔍 Using existing participant data:', participant);
      // Fallback: use the participant data we already have from leaderboard
      dispatch(setSelectedParticipant(participant));
    }
  };

  const handleCloseReceipts = () => {
    dispatch(setSelectedParticipant(null));
  };

  const handleRetry = () => {
    dispatch(fetchLeaderboard({ tier: 'All', page: 0, size: 50 }));
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-theme-primary">
      {/* SEO Meta Tags */}
      <SEOMeta
        title={seoMeta.title}
        description={seoMeta.description}
        keywords={seoMeta.keywords}
      />

      {/* Compact Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => navigate('/hall-of-fame')}
              className="flex items-center gap-1 md:gap-2 text-white/90 hover:text-white transition-colors flex-shrink-0"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Overview</span>
            </button>

            <div className="text-center flex-1 min-w-0">
              <h1 className="text-xl md:text-3xl font-bold flex items-center justify-center gap-2">
                <Trophy size={24} className="md:w-8 md:h-8" />
                Leaderboard
              </h1>
              <p className="text-xs md:text-sm opacity-90 mt-1 hidden sm:block">
                See how investors rank against each other in {currentYear}
              </p>
            </div>

            {/* Quick Stats - Desktop Only */}
            <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
              <div className="text-right">
                <div className="text-2xl font-bold">
                  {leaderboardData?.participants.length || 0}
                </div>
                <div className="text-xs opacity-75">Participants</div>
              </div>

              <div className="text-right">
                <div className="text-2xl font-bold">
                  {leaderboardData?.communityStats?.averageROI?.toFixed(2) || '0.00'}%
                </div>
                <div className="text-xs opacity-75">Community Avg</div>
              </div>

              <div className="text-right">
                <div className="text-2xl font-bold">
                  {leaderboardData?.participants?.filter((p) => p.tier === 'S').length || 0}
                </div>
                <div className="text-xs opacity-75">S-Tier</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-8 text-center mb-8">
            <div className="text-red-400 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold mb-2">Backend Connection Failed</h3>
              <p className="mb-2">{error}</p>
              <p className="text-sm text-gray-400">
                Make sure your Spring Boot backend is running on <code className="bg-black/30 px-2 py-1 rounded">http://localhost:5000</code>
              </p>
            </div>
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium transition-colors"
            >
              Retry Connection
            </button>
            <div className="mt-4 text-left text-sm text-gray-400 bg-black/50 p-4 rounded">
              <p className="font-mono">🔍 Troubleshooting:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Check if backend is running: <code>curl http://localhost:5000/api/leaderboard</code></li>
                <li>Verify MongoDB is connected</li>
                <li>Check Spring Boot console for errors</li>
                <li>Look at browser console (F12) for detailed error</li>
              </ul>
            </div>
          </div>
        )}

        {/* Tier Legend - Info Box */}
        {!error && leaderboardData && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Tier System</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">👑 S-Tier:</span>
                    <span className="text-theme-muted">+50% or higher</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">🥈 A-Tier:</span>
                    <span className="text-theme-muted">+30% to +49.99%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">🥉 B-Tier:</span>
                    <span className="text-theme-muted">+20% to +29.99%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">📊 C-Tier:</span>
                    <span className="text-theme-muted">+0% to +19.99%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">⚠️ D-Tier:</span>
                    <span className="text-theme-muted">-20% to -0.01%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">💀 F-Tier:</span>
                    <span className="text-theme-muted">Below -20%</span>
                  </div>
                </div>
                <p className="text-xs text-theme-muted mt-2 italic">Hover over any tier badge for details</p>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {!error && leaderboardData && (
          <Leaderboard
            participants={leaderboardData.participants || []}
            communityAverageROI={leaderboardData.communityStats?.averageROI || 0}
            onSelectParticipant={handleSelectParticipant}
          />
        )}
      </div>

      {/* Receipts Dashboard Modal */}
      {selectedParticipant && leaderboardData && (
        <ReceiptsDashboard
          participant={selectedParticipant}
          communityAverage={{
            roi: leaderboardData.communityStats?.averageROI || 0,
            history: leaderboardData.communityAverageHistory || [],
          }}
          onClose={handleCloseReceipts}
        />
      )}
    </div>
  );
};

export default HallOfFameLeaderboard;

